/**
 * RuleFormDialog Component
 *
 * Create or edit a scheduling rule. Dynamic parameter forms per rule type.
 *
 * US-PPL-001 Phase 5: Scheduling Rules Configuration
 */

import { useState, useEffect } from 'react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select'
import { Loader2 } from 'lucide-react'

const RULE_TYPES = [
  { value: 'avoid_day', label: 'Avoid Day' },
  { value: 'prefer_day', label: 'Prefer Day' },
  { value: 'max_gap', label: 'Maximum Gap' },
  { value: 'min_gap', label: 'Minimum Gap' },
  { value: 'capacity_limit', label: 'Capacity Limit' },
]

const DAYS_OF_WEEK = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
]

const TASK_TYPES = [
  'harvest', 'move_to_fruiting', 'inoculation', 'shoogle',
  'inspection', 'substrate_prep', 'pasteurisation', 'cleaning'
]

const PHASE_TYPES = [
  { value: 'lc_incubation', label: 'LC Incubation' },
  { value: 'spawn_run', label: 'Spawn Run' },
  { value: 'substrate_incubation', label: 'Substrate Incubation' },
  { value: 'fruiting', label: 'Fruiting' },
  { value: 'rest_period', label: 'Rest Period' },
  { value: 'other', label: 'Other' },
]

export default function RuleFormDialog({ open, onOpenChange, rule, onSave, saving }) {
  const isEditing = !!rule

  const [formData, setFormData] = useState({
    rule_name: '',
    description: '',
    rule_type: '',
    constraint_type: 'soft',
    priority_rank: 100,
    is_active: true,
    parameters: {}
  })

  useEffect(() => {
    if (rule) {
      setFormData({
        rule_name: rule.rule_name || '',
        description: rule.description || '',
        rule_type: rule.rule_type || '',
        constraint_type: rule.constraint_type || 'soft',
        priority_rank: rule.priority_rank || 100,
        is_active: rule.is_active !== false,
        parameters: rule.parameters || {}
      })
    } else {
      setFormData({
        rule_name: '', description: '', rule_type: '',
        constraint_type: 'soft', priority_rank: 100, is_active: true,
        parameters: {}
      })
    }
  }, [rule, open])

  const updateParam = (key, value) => {
    setFormData(prev => ({
      ...prev,
      parameters: { ...prev.parameters, [key]: value }
    }))
  }

  const toggleDayParam = (day) => {
    const current = formData.parameters.days || []
    const updated = current.includes(day)
      ? current.filter(d => d !== day)
      : [...current, day]
    updateParam('days', updated)
  }

  const toggleTaskType = (type) => {
    const current = formData.parameters.task_types || []
    const updated = current.includes(type)
      ? current.filter(t => t !== type)
      : [...current, type]
    updateParam('task_types', updated)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave({
      ...(rule?.id ? { id: rule.id } : {}),
      ...formData
    })
  }

  const canSubmit = formData.rule_name && formData.rule_type

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Rule' : 'Create Rule'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Modify scheduling rule parameters' : 'Define a new scheduling constraint'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="rule_name">Name</Label>
            <Input
              id="rule_name"
              value={formData.rule_name}
              onChange={e => setFormData(prev => ({ ...prev, rule_name: e.target.value }))}
              placeholder="e.g. Avoid weekend harvest"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Why this rule exists..."
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Rule Type</Label>
              <Select
                value={formData.rule_type}
                onValueChange={v => setFormData(prev => ({ ...prev, rule_type: v, parameters: {} }))}
                disabled={isEditing}
              >
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {RULE_TYPES.map(rt => (
                    <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Constraint</Label>
              <Select
                value={formData.constraint_type}
                onValueChange={v => setFormData(prev => ({ ...prev, constraint_type: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hard">Hard (must obey)</SelectItem>
                  <SelectItem value="soft">Soft (prefer)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priority">Priority Rank</Label>
              <Input
                id="priority"
                type="number"
                min={1}
                value={formData.priority_rank}
                onChange={e => setFormData(prev => ({ ...prev, priority_rank: parseInt(e.target.value) || 100 }))}
              />
              <p className="text-xs text-muted-foreground">Lower = higher priority</p>
            </div>

            <div className="flex items-center gap-3 pt-6">
              <Switch
                checked={formData.is_active}
                onCheckedChange={v => setFormData(prev => ({ ...prev, is_active: v }))}
              />
              <Label>Active</Label>
            </div>
          </div>

          {/* Dynamic parameters */}
          {(formData.rule_type === 'avoid_day' || formData.rule_type === 'prefer_day') && (
            <div className="space-y-3 border rounded-lg p-3">
              <Label className="text-sm font-medium">
                {formData.rule_type === 'avoid_day' ? 'Days to Avoid' : 'Preferred Days'}
              </Label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map(day => (
                  <label key={day.value} className="flex items-center gap-1.5 text-sm">
                    <Checkbox
                      checked={(formData.parameters.days || []).includes(day.value)}
                      onCheckedChange={() => toggleDayParam(day.value)}
                    />
                    {day.label}
                  </label>
                ))}
              </div>

              <Label className="text-sm font-medium">Task Types</Label>
              <div className="flex flex-wrap gap-2">
                {TASK_TYPES.map(type => (
                  <label key={type} className="flex items-center gap-1.5 text-sm">
                    <Checkbox
                      checked={(formData.parameters.task_types || []).includes(type)}
                      onCheckedChange={() => toggleTaskType(type)}
                    />
                    {type.replace('_', ' ')}
                  </label>
                ))}
              </div>
            </div>
          )}

          {(formData.rule_type === 'max_gap' || formData.rule_type === 'min_gap') && (
            <div className="space-y-3 border rounded-lg p-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>From Phase</Label>
                  <Select
                    value={formData.parameters.from_phase_type || ''}
                    onValueChange={v => updateParam('from_phase_type', v)}
                  >
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {PHASE_TYPES.map(pt => (
                        <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>To Phase</Label>
                  <Select
                    value={formData.parameters.to_phase_type || ''}
                    onValueChange={v => updateParam('to_phase_type', v)}
                  >
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {PHASE_TYPES.map(pt => (
                        <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>
                  {formData.rule_type === 'max_gap' ? 'Maximum Days' : 'Minimum Days'}
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={formData.parameters[formData.rule_type === 'max_gap' ? 'max_days' : 'min_days'] || ''}
                  onChange={e => updateParam(
                    formData.rule_type === 'max_gap' ? 'max_days' : 'min_days',
                    parseInt(e.target.value) || 0
                  )}
                />
              </div>
            </div>
          )}

          {formData.rule_type === 'capacity_limit' && (
            <div className="space-y-3 border rounded-lg p-3">
              <div className="space-y-2">
                <Label>Maximum Tasks per Day</Label>
                <Input
                  type="number"
                  min={1}
                  value={formData.parameters.max_tasks || ''}
                  onChange={e => updateParam('max_tasks', parseInt(e.target.value) || 20)}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit || saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
